import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import { useStory } from '../../hooks/useStory';
import { useStreams } from '../../hooks/useStreams';
import { relativeTime } from '@news-assist/shared';
import { apiClient } from '../../api/client';
import { Layers, Tag, X, List, ScatterChart } from 'lucide-react';
import { ClusterView } from './ClusterView';

interface EditingClaim {
  id: string;
  content: string;
}

interface SimilarStory {
  id: string;
  title: string;
  slug: string;
  similarity: number;
  claim_count: number;
}

export function StoryPage() {
  const { id, orgSlug } = useParams<{ id: string; orgSlug: string }>();
  const navigate = useNavigate();
  const orgPrefix = orgSlug ? `/${orgSlug}` : '';
  const { data: story, isLoading, isError, error } = useStory(id || '');
  const queryClient = useQueryClient();

  // Editing state
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [isEditingSummary, setIsEditingSummary] = useState(false);
  const [editedSummary, setEditedSummary] = useState('');
  const [editingClaim, setEditingClaim] = useState<EditingClaim | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleEditTitle = () => {
    setEditedTitle(story?.title || '');
    setIsEditingTitle(true);
  };

  const handleSaveTitle = async () => {
    if (!story) return;
    setIsSaving(true);
    try {
      await apiClient.patch(`/stories/${story.id}`, { title: editedTitle });
      queryClient.invalidateQueries({ queryKey: ['story', id] });
      queryClient.invalidateQueries({ queryKey: ['stories'] });
      setIsEditingTitle(false);
    } catch (err) {
      alert('Failed to save: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditSummary = () => {
    setEditedSummary(story?.summary || '');
    setIsEditingSummary(true);
  };

  const handleSaveSummary = async () => {
    if (!story) return;
    setIsSaving(true);
    try {
      await apiClient.patch(`/stories/${story.id}`, { summary: editedSummary });
      queryClient.invalidateQueries({ queryKey: ['story', id] });
      setIsEditingSummary(false);
    } catch (err) {
      alert('Failed to save: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditClaim = (claim: { id: string; content: string }) => {
    setEditingClaim({ id: claim.id, content: claim.content });
  };

  const handleSaveClaim = async () => {
    if (!editingClaim) return;
    setIsSaving(true);
    try {
      await apiClient.patch(`/claims/${editingClaim.id}`, { content: editingClaim.content });
      queryClient.invalidateQueries({ queryKey: ['story', id] });
      setEditingClaim(null);
    } catch (err) {
      alert('Failed to save: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClaim = async (claimId: string) => {
    if (!confirm('Delete this claim?')) return;
    try {
      await apiClient.delete(`/claims/${claimId}`);
      queryClient.invalidateQueries({ queryKey: ['story', id] });
    } catch (err) {
      alert('Failed to delete: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  const handleDeleteStory = async () => {
    if (!confirm('Delete this entire story and all its claims? This cannot be undone.')) return;
    try {
      await apiClient.delete(`/stories/${id}`);
      queryClient.invalidateQueries({ queryKey: ['feed'] });
      navigate(orgPrefix || '/');
    } catch (err) {
      alert('Failed to delete: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  const [isRegenerating, setIsRegenerating] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'cluster'>('list');
  const [similarStories, setSimilarStories] = useState<SimilarStory[]>([]);
  const [isMerging, setIsMerging] = useState(false);

  // Fetch similar stories when story loads
  useEffect(() => {
    if (story?.id) {
      apiClient.get<{ similar: SimilarStory[] }>(`/stories/${story.id}/similar`)
        .then(data => setSimilarStories(data.similar || []))
        .catch(() => setSimilarStories([]));
    }
  }, [story?.id]);

  const handleMergeStory = async (sourceStoryId: string, sourceTitle: string) => {
    if (!story) return;
    if (!confirm(`Merge "${sourceTitle}" into this story? The other story will be deleted.`)) return;

    setIsMerging(true);
    try {
      await apiClient.post('/stories/merge', {
        target_story_id: story.id,
        source_story_ids: [sourceStoryId],
      });
      queryClient.invalidateQueries({ queryKey: ['story', id] });
      queryClient.invalidateQueries({ queryKey: ['stories'] });
      // Refresh similar stories
      const data = await apiClient.get<{ similar: SimilarStory[] }>(`/stories/${story.id}/similar`);
      setSimilarStories(data.similar || []);
    } catch (err) {
      alert('Failed to merge: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setIsMerging(false);
    }
  };

  // Stream assignment state
  const { data: streamsData } = useStreams();
  const streams = streamsData?.streams || [];
  const [isAssigningStream, setIsAssigningStream] = useState(false);
  const [showStreamPicker, setShowStreamPicker] = useState(false);

  const handleAssignStream = async (streamId: string | null) => {
    if (!story) return;
    setIsAssigningStream(true);
    try {
      await apiClient.patch(`/stories/${story.id}/stream`, { streamId });
      queryClient.invalidateQueries({ queryKey: ['story', id] });
      queryClient.invalidateQueries({ queryKey: ['stories'] });
      queryClient.invalidateQueries({ queryKey: ['streams'] });
      setShowStreamPicker(false);
    } catch (err) {
      alert('Failed to assign stream: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setIsAssigningStream(false);
    }
  };

  const handleCreateStreamFromStory = async () => {
    if (!story) return;
    setIsAssigningStream(true);
    try {
      const result = await apiClient.post<{ id: string }>('/streams/from-story', { storyId: story.id });
      queryClient.invalidateQueries({ queryKey: ['story', id] });
      queryClient.invalidateQueries({ queryKey: ['stories'] });
      queryClient.invalidateQueries({ queryKey: ['streams'] });
      setShowStreamPicker(false);
      navigate(`${orgPrefix}/stream/${result.id}`);
    } catch (err) {
      alert('Failed to create stream: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setIsAssigningStream(false);
    }
  };

  // Entity and topic types for display
  interface StoryEntity {
    id: string;
    name: string;
    type: string;
    role: string;
  }

  interface StoryTopic {
    id: string;
    name: string;
    type: string;
    isPrimary: boolean;
  }

  const storyEntities: StoryEntity[] = (story as { entities?: StoryEntity[] })?.entities || [];
  const storyTopics: StoryTopic[] = (story as { topics?: StoryTopic[] })?.topics || [];

  // Colors for topics and entities
  const topicTypeColors: Record<string, string> = {
    subject: 'bg-purple-100 text-purple-800 border-purple-200',
    geography: 'bg-amber-100 text-amber-800 border-amber-200',
    organization: 'bg-green-100 text-green-800 border-green-200',
    person: 'bg-blue-100 text-blue-800 border-blue-200',
  };

  const entityTypeColors: Record<string, string> = {
    person: 'bg-blue-50 text-blue-700',
    organization: 'bg-green-50 text-green-700',
    location: 'bg-amber-50 text-amber-700',
  };

  const entityRoleLabels: Record<string, string> = {
    subject: '●',
    actor: '◐',
    quoted: '"',
    mentioned: '',
  };

  const handleRegenerateSummary = async () => {
    if (!story) return;
    setIsRegenerating(true);
    try {
      await apiClient.post(`/stories/${story.id}/regenerate-summary`);
      queryClient.invalidateQueries({ queryKey: ['story', id] });
    } catch (err) {
      alert('Failed to regenerate: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setIsRegenerating(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="text-gray-500">Loading story...</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        Error: {error.message}
      </div>
    );
  }

  if (!story) {
    return (
      <div className="text-center py-12 text-gray-500">Story not found</div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Main content */}
      <div className="lg:col-span-2 space-y-6">
        {/* Story header */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-start justify-between">
            {isEditingTitle ? (
              <div className="flex-1 mr-4">
                <input
                  type="text"
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e.target.value)}
                  className="w-full p-2 text-2xl font-bold border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  autoFocus
                />
                <div className="mt-2 flex gap-2">
                  <button
                    onClick={handleSaveTitle}
                    disabled={isSaving}
                    className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => setIsEditingTitle(false)}
                    className="px-3 py-1.5 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <h1
                className="text-2xl font-bold text-gray-900 cursor-pointer hover:text-blue-600 transition-colors"
                onClick={handleEditTitle}
                title="Click to edit title"
              >
                {story.title}
              </h1>
            )}
            <button
              onClick={handleDeleteStory}
              className="ml-4 p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
              title="Delete story"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>

          {/* Stream assignment */}
          <div className="mt-3 relative">
            {(story as { streamId?: string; streamTitle?: string }).streamId ? (
              <div className="flex items-center gap-2">
                <Layers className="w-4 h-4 text-purple-600" />
                <Link
                  to={`${orgPrefix}/stream/${(story as { streamId: string }).streamId}`}
                  className="text-sm text-purple-600 hover:underline"
                >
                  {(story as { streamTitle?: string }).streamTitle}
                </Link>
                <button
                  onClick={() => setShowStreamPicker(!showStreamPicker)}
                  className="text-xs text-gray-400 hover:text-gray-600"
                >
                  Change
                </button>
                <button
                  onClick={() => handleAssignStream(null)}
                  disabled={isAssigningStream}
                  className="text-xs text-gray-400 hover:text-red-600"
                >
                  Remove
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowStreamPicker(!showStreamPicker)}
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-purple-600"
              >
                <Layers className="w-4 h-4" />
                Add to stream
              </button>
            )}

            {showStreamPicker && (
              <div className="absolute z-10 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 p-3">
                <div className="text-sm font-medium text-gray-700 mb-2">Assign to stream</div>
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {streams.map((stream) => (
                    <button
                      key={stream.id}
                      onClick={() => handleAssignStream(stream.id)}
                      disabled={isAssigningStream}
                      className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-purple-50 hover:text-purple-700 disabled:opacity-50"
                    >
                      {stream.title}
                    </button>
                  ))}
                  {streams.length === 0 && (
                    <div className="text-sm text-gray-400 py-2">No streams yet</div>
                  )}
                </div>
                <div className="border-t mt-2 pt-2">
                  <button
                    onClick={handleCreateStreamFromStory}
                    disabled={isAssigningStream}
                    className="w-full text-left px-2 py-1.5 text-sm text-purple-600 font-medium rounded hover:bg-purple-50 disabled:opacity-50"
                  >
                    + Create new stream from this story
                  </button>
                </div>
                <button
                  onClick={() => setShowStreamPicker(false)}
                  className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
          </div>

          {/* Topics - What the story is about */}
          {storyTopics.length > 0 && (
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <span className="text-xs text-gray-500 font-medium">Topics:</span>
              {storyTopics.filter(t => t.type === 'subject').map((topic) => (
                <span
                  key={topic.id}
                  className={`px-2 py-0.5 text-xs font-medium rounded border ${topicTypeColors[topic.type] || 'bg-gray-100 text-gray-700 border-gray-200'}`}
                >
                  {topic.name}
                </span>
              ))}
              {storyTopics.filter(t => t.type === 'geography').map((topic) => (
                <span
                  key={topic.id}
                  className={`px-2 py-0.5 text-xs rounded border ${topicTypeColors[topic.type] || 'bg-gray-100 text-gray-700 border-gray-200'}`}
                >
                  {topic.name}
                </span>
              ))}
            </div>
          )}

          {/* Entities - Who is involved */}
          {storyEntities.length > 0 && (
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <span className="text-xs text-gray-500 font-medium">People & Orgs:</span>
              {storyEntities.filter(e => e.role === 'subject' || e.role === 'actor' || e.role === 'quoted').slice(0, 8).map((entity) => (
                <span
                  key={entity.id}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded ${entityTypeColors[entity.type] || 'bg-gray-100 text-gray-700'}`}
                  title={`${entity.type} (${entity.role})`}
                >
                  {entityRoleLabels[entity.role] && (
                    <span className="opacity-60">{entityRoleLabels[entity.role]}</span>
                  )}
                  {entity.name}
                </span>
              ))}
              {storyEntities.filter(e => e.role === 'subject' || e.role === 'actor' || e.role === 'quoted').length > 8 && (
                <span className="text-xs text-gray-400">
                  +{storyEntities.filter(e => e.role === 'subject' || e.role === 'actor' || e.role === 'quoted').length - 8} more
                </span>
              )}
            </div>
          )}

          {/* Summary - editable */}
          {isEditingSummary ? (
            <div className="mt-3">
              <textarea
                value={editedSummary}
                onChange={(e) => setEditedSummary(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={5}
                placeholder="Story summary..."
              />
              <div className="mt-2 flex gap-2">
                <button
                  onClick={handleSaveSummary}
                  disabled={isSaving}
                  className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => setIsEditingSummary(false)}
                  className="px-3 py-1.5 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="mt-2">
              <div className="text-gray-600 [&_p]:mb-4 [&_p:last-child]:mb-0">
                <ReactMarkdown>
                  {story.summary || 'No summary yet'}
                </ReactMarkdown>
              </div>
            </div>
          )}

          <div className="mt-4 flex items-center justify-end text-sm text-gray-500">
            <div className="flex gap-2">
              <button
                onClick={handleRegenerateSummary}
                disabled={isRegenerating}
                className="px-2 py-1 text-xs text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded disabled:opacity-50"
                title="Regenerate summary with AI"
              >
                {isRegenerating ? 'Regenerating...' : 'Regenerate'}
              </button>
              <button
                onClick={handleEditSummary}
                className="px-2 py-1 text-xs text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
              >
                Edit
              </button>
            </div>
          </div>
        </div>

        {/* Claims Section */}
        <div className="bg-white rounded-lg shadow" data-testid="claim-timeline">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold">
              Claims
            </h2>
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('list')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors ${
                  viewMode === 'list'
                    ? 'bg-white shadow text-gray-900'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <List className="w-4 h-4" />
                List
              </button>
              <button
                onClick={() => setViewMode('cluster')}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors ${
                  viewMode === 'cluster'
                    ? 'bg-white shadow text-gray-900'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <ScatterChart className="w-4 h-4" />
                Cluster
              </button>
            </div>
          </div>

          {viewMode === 'cluster' ? (
            <div className="p-4">
              <ClusterView storyId={story.id} />
            </div>
          ) : (
          <div className="divide-y divide-gray-200">
            {story.claims?.map((claim) => (
              <div key={claim.id} className="p-4 group">
                {editingClaim?.id === claim.id ? (
                  <div>
                    <textarea
                      value={editingClaim.content}
                      onChange={(e) => setEditingClaim({ ...editingClaim, content: e.target.value })}
                      className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                      rows={3}
                    />
                    <div className="mt-2 flex gap-2">
                      <button
                        onClick={handleSaveClaim}
                        disabled={isSaving}
                        className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:opacity-50"
                      >
                        {isSaving ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={() => setEditingClaim(null)}
                        className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded hover:bg-gray-300"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-3">
                    {/* Type badge */}
                    <span
                      className={`px-1.5 py-0.5 text-xs rounded flex-shrink-0 font-medium ${
                        claim.claim_type === 'QUOTE' ? 'bg-purple-100 text-purple-700' :
                        claim.claim_type === 'OFFICIAL' ? 'bg-blue-100 text-blue-700' :
                        claim.claim_type === 'ALLEGATION' ? 'bg-red-100 text-red-700' :
                        claim.claim_type === 'STATISTIC' ? 'bg-cyan-100 text-cyan-700' :
                        claim.claim_type === 'FINDING' ? 'bg-amber-100 text-amber-700' :
                        'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {claim.claim_type || 'NEWS'}
                    </span>
                    <div className="flex-1 min-w-0">
                      {/* Question this claim answers */}
                      {claim.question && (
                        <p className="text-xs text-gray-500 italic mb-1">{claim.question}</p>
                      )}
                      {/* Claim content */}
                      <p className="text-gray-900">{claim.content}</p>
                      {/* Metadata row */}
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                        {/* Subject */}
                        {claim.subject?.name && (
                          <span className="inline-flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-gray-400"></span>
                            {claim.subject.name}
                          </span>
                        )}
                        {/* Priority/Urgency */}
                        {claim.urgency && (
                          <span className={`inline-flex items-center gap-1 ${
                            claim.urgency >= 4 ? 'text-red-600' :
                            claim.urgency >= 3 ? 'text-amber-600' :
                            'text-gray-500'
                          }`}>
                            <span>Priority {claim.urgency}</span>
                          </span>
                        )}
                        {/* Relevancy/Recency */}
                        {claim.recency_score != null && (
                          <span className="text-gray-400">
                            {Math.round(claim.recency_score * 100)}% relevancy
                          </span>
                        )}
                        {/* Source count */}
                        {claim.source_count && claim.source_count > 0 && (
                          <span>{claim.source_count} source{claim.source_count > 1 ? 's' : ''}</span>
                        )}
                      </div>
                    </div>
                    {/* Edit/Delete buttons */}
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => handleEditClaim(claim)}
                        className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                        title="Edit"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDeleteClaim(claim.id)}
                        className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                        title="Delete"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {story.quotes?.map((quote) => (
              <div key={quote.id} className="p-4">
                <div className="flex items-start gap-3">
                  <span className="px-1.5 py-0.5 text-xs rounded bg-purple-100 text-purple-700">
                    QUOTE
                  </span>
                  <div>
                    <p className="text-gray-900 italic">"{quote.content}"</p>
                    <p className="mt-1 text-xs text-gray-500">
                      — {quote.speakerName}
                      {quote.speakerTitle && `, ${quote.speakerTitle}`}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          )}
        </div>
      </div>

      {/* Sidebar */}
      <div className="space-y-6 lg:sticky lg:top-20 lg:h-[calc(100vh-6rem)] lg:overflow-y-auto">
        {/* Source Materials */}
        <div className="bg-white rounded-lg shadow">
          <h2 className="px-4 py-3 text-sm font-semibold text-gray-700 border-b border-gray-200">
            Source Materials
          </h2>
          <div className="divide-y divide-gray-100">
            {story.materials?.map((material) => (
              <div key={material.id} className="p-4">
                <div className="flex items-start gap-2">
                  <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded flex-shrink-0">
                    {material.sourceName}
                  </span>
                </div>
                <Link
                  to={`${orgPrefix}/material/${material.id}`}
                  className="block mt-2 text-sm text-blue-600 hover:underline line-clamp-2"
                >
                  {material.title}
                </Link>
                <div className="mt-1 flex items-center gap-2 text-xs text-gray-400">
                  <time>{material.publishedAt ? relativeTime(material.publishedAt) : 'Unknown date'}</time>
                  {material.url && (
                    <>
                      <span>·</span>
                      <a
                        href={material.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-gray-600"
                      >
                        Original
                      </a>
                    </>
                  )}
                </div>
              </div>
            ))}
            {(!story.materials || story.materials.length === 0) && (
              <p className="p-4 text-sm text-gray-500">No source materials found</p>
            )}
          </div>
        </div>

        {/* Sources */}
        {story.sources && story.sources.length > 0 && (
          <div className="bg-white rounded-lg shadow">
            <h2 className="px-4 py-3 text-sm font-semibold text-gray-700 border-b border-gray-200">
              Sources
            </h2>
            <div className="p-4 space-y-2">
              {story.sources.map((source: { id: string; name: string; slug: string; material_count?: number }) => (
                <div key={source.id} className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">{source.name}</span>
                  {source.material_count && (
                    <span className="text-xs text-gray-400">
                      {source.material_count} material{source.material_count !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Similar Stories */}
        {similarStories.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg shadow">
            <h2 className="px-4 py-3 text-sm font-semibold text-amber-800 border-b border-amber-200 flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Similar Stories
            </h2>
            <div className="p-4 space-y-3">
              <p className="text-xs text-amber-700">
                These stories might be related or duplicates. Click "Merge" to combine them.
              </p>
              {similarStories.map((similar) => (
                <div key={similar.id} className="bg-white rounded p-3 border border-amber-200">
                  <Link
                    to={`${orgPrefix}/story/${similar.slug || similar.id}`}
                    className="text-sm text-gray-900 hover:text-blue-600 font-medium line-clamp-2"
                  >
                    {similar.title}
                  </Link>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-xs text-gray-500">
                      {Math.round(similar.similarity * 100)}% similar · {similar.claim_count} claims
                    </span>
                    <button
                      onClick={() => handleMergeStory(similar.id, similar.title)}
                      disabled={isMerging}
                      className="px-2 py-1 text-xs bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50"
                    >
                      {isMerging ? 'Merging...' : 'Merge'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
